import type { JSX } from "react";
import type { PanelProps, PanelToolbarAction } from "./panel/panel";
import { Fade, Modal, css, styled } from "@mui/material";
import { useMemo } from "react";
import classNames from "classnames";
import { CloseIcon } from "../atoms/icons/mui-icons";
import { Panel } from "./panel/panel";


const PanelDialogContainer = styled("div")(({ theme }) => css`
	display: grid;
	${theme.breakpoints.down("sm")} {
		&, > * {
			width: 100%;
			height: 100%;
		}

		.dialogPanel__panel {
			border-radius: 0;
		}

	}

	&, > * {
		max-height: 100dvh; 
		max-width: 100vw; 
	}

	.dialogPanel__content {
		overflow-y: auto;
		width: 100%;
		height: 100%;
		display: grid;
		place-items: start stretch;
	}

	${theme.breakpoints.up("sm")} {
		width: max-content;
		height: max-content;
		position: absolute;
		left: 50%;
		top: 50%;
		transform: translate(-50%, -50%);
	
		.dialogPanel__content {
			max-width: 90vw;
			max-height: 66vh;

			&.narrow {
				max-width: 520px;
			}
			
			&.fullHeight {
				height: 66vh;
			}
		}
	}
`);

export type PanelDialogProps = Omit<PanelProps, "className" | "elevation"> & {
	open: boolean;
	narrow?: boolean;
	fullHeight?: boolean;
} & (
		| {
			dismissible: true | "disabled";
			onClose: VoidFunction;
		} | {
			dismissible?: false;
			onClose?: VoidFunction;
		}
	);

/**
 * A <{@link Panel}> in a modal dialog.
 */
export const PanelDialog = ({ open, onClose, dismissible, toolbar, disabled, loading, narrow, fullHeight, ...panelProps }: PanelDialogProps): JSX.Element => {
	const computedDismissible = dismissible === true && !disabled && !loading;
	const computedToolbar = useMemo<readonly PanelToolbarAction[] | undefined>(() => {
		if(!dismissible) {
			return toolbar;
		}

		const dismissAction: PanelToolbarAction = {
			labelKey: "common:close",
			icon: CloseIcon,
			onClick: onClose,
			disabled: dismissible === "disabled",
		};

		if(toolbar) {
			return [ ...toolbar, dismissAction ];
		}

		return [ dismissAction ];
	}, [ toolbar, dismissible, onClose ]);

	return (
		<Modal
			open={ open }
			onClose={ computedDismissible ? onClose : undefined }
			closeAfterTransition
		>
			<Fade in={ open }>
				<PanelDialogContainer>
					<Panel
						{ ...panelProps as PanelProps }
						className={ "dialogPanel__panel" }
						innerClassName={ classNames(panelProps.innerClassName, "dialogPanel__content", { narrow, fullHeight }) }
						toolbar={ computedToolbar }
						loading={ loading }
						disabled={ disabled }
						elevation={ 12 }
					/>
				</PanelDialogContainer>
			</Fade>
		</Modal>
	);
};
